import {
  Box, Button, Card, CardContent, CardHeader, IconButton,
  InputAdornment, Tooltip, Dialog, DialogActions, DialogTitle,
  DialogContent, Typography, Chip, Paper, TextField, Container,
  Stack, Avatar, ToggleButton, ToggleButtonGroup, Grid, Fade,
  Zoom, FormControl, Select, MenuItem, Input
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { ADMIN_ROLES } from "../../utils/constants";
import { capitalize, debounce } from "lodash";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import { walletServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import TableFilter from "../../components/shared/TableFilter";
import { PAYMENT_WITHDRAWAL_STATUS_OPTIONS } from "../../utils/constants";
import { Loader } from "../../components/CustomLoader";
import { fDate } from "../../utils/formatTime";
import { formatCash } from "../../utils/formatNumber";
import { TrimText } from "../../utils/helpers";
import { Helmet } from "react-helmet-async";

// A withdrawal is returned to the original payment sources, so a request can
// carry several payments (splitDetails) - one per source. The admin panel shows
// each of them separately rather than collapsing them into a single method.
const PAYMENT_METHOD_LABELS = {
  cards: 'Card',
  applePay: 'Apple Pay',
  googlePay: 'Google Pay',
  wallet: 'Wallet',
  other: 'Other',
};

const formatPaymentMethod = (method) =>
  PAYMENT_METHOD_LABELS[method] || (method ? capitalize(method) : '-');

const getSplitDetails = (request) =>
  Array.isArray(request?.splitDetails) ? request.splitDetails : [];

const splitKey = (split, index) => split?._id || `${split?.method}-${index}`;

// Compact "method + amount" list used in the cards, the table and the dialog.
function PaymentBreakdown({ request, align = 'left' }) {
  const splits = getSplitDetails(request);

  if (!splits.length) {
    return <Typography variant="body2" sx={{ color: 'text.disabled' }}>-</Typography>;
  }

  return (
    <Stack spacing={0.5} sx={{ width: '100%', py: align === 'left' ? 0.5 : 0 }}>
      {splits.map((split, index) => (
        <Stack
          key={splitKey(split, index)}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Tooltip title={split.pspid ? `PSP ID: ${split.pspid}` : ''}>
            <Chip
              label={formatPaymentMethod(split.method)}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 600,
                backgroundColor: 'rgba(0, 161, 221, 0.15)',
                color: '#00a1dd',
                border: '1px solid',
                borderColor: 'rgba(0, 161, 221, 0.3)',
              }}
            />
          </Tooltip>
          <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            {formatCash(split.amount || 0)}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

// Expanded table row: the same payments with their PSP reference.
function SplitDetailsRow({ data: request }) {
  const splits = getSplitDetails(request);

  if (!splits.length) {
    return (
      <Box sx={{ px: 3, py: 2 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No payment breakdown available for this request.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 3, py: 2, backgroundColor: 'rgba(0, 161, 221, 0.04)' }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
        Payments ({splits.length})
      </Typography>
      <Stack spacing={1}>
        {splits.map((split, index) => (
          <Stack
            key={splitKey(split, index)}
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={{ xs: 0.5, sm: 3 }}
          >
            <Typography variant="body2" sx={{ minWidth: 120, fontWeight: 600 }}>
              {formatPaymentMethod(split.method)}
            </Typography>
            <Typography variant="body2" sx={{ minWidth: 100 }}>
              {formatCash(split.amount || 0)}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              PSP ID: {split.pspid || '-'}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

export default function WithdrawalRequestList() {
  const user = useSelector((state) => state.auth.user);
  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [totalRequests, setTotalRequests] = useState(0);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [approvedRequests, setApprovedRequests] = useState(0);
  const [rejectedRequests, setRejectedRequests] = useState(0);
  const [approvalDialog, setApprovalDialog] = useState({
    open: false, type: null, request: null, message: ""
  });

  const fetchStatistics = async () => {
    try {
      // Get all data for statistics calculation (without pagination)
      const res = await walletServices.getAllWithdrawalRequest({
        searchItem: "",
        pageNumber: 1,
        pageSize: 10000, // Large number to get all records
        status: [],
      });

      if (res.status === 200 && res?.data?.data) {
        const allRequests = res.data.data;
        setTotalRequests(allRequests.length);

        const pending = allRequests.filter(req => req.status === 'pending').length;
        const approved = allRequests.filter(req => req.status === 'approved').length;
        const rejected = allRequests.filter(req => req.status === 'rejected').length;

        setPendingRequests(pending);
        setApprovedRequests(approved);
        setRejectedRequests(rejected);
      }
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  };

  const fetchUsers = async (query) => {
    setIsLoading(true);
    try {
      const res = await walletServices.getAllWithdrawalRequest({
        searchItem: query || "",
        pageNumber: currentPage,
        pageSize,
        status,
      });
      if (res.status === 200) {
        setData(res?.data?.data);
        setTotalPages(res?.data?.totalPages);
        setTotalCount(res?.data?.total);
      }
    } catch (err) {
      errorHandler(err);
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => fetchUsers(query), 400),
    [currentPage, pageSize, status]
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    debouncedSearch(query);
  };

  const handleApprovalAction = (request, action) => {
    setApprovalDialog({ open: true, type: action, request });
  };

  const confirmApprovalAction = async () => {
    const { type, request, message } = approvalDialog;
    try {
      const payload = { requestId: request._id, action: type, reason: message };
      const res = await walletServices.withdrawlRequest(payload);
      if (res.status === 200) {
        toast.success(`${capitalize(type)} successful`);
        setApprovalDialog({ open: false, type: null, request: null, message: "" });
        setRefresh(prev => !prev);
      }
    } catch (err) {
      errorHandler(err);
    }
  };

  // Fetch statistics on component mount
  useEffect(() => {
    fetchStatistics();
  }, []);

  useEffect(() => {
    fetchUsers(searchItem);
    fetchStatistics(); // Fetch statistics on every data refresh
  }, [currentPage, pageSize, status, refresh]);

  const statusChip = (status) => {
    const colors = {
      approved: "success", rejected: "error", pending: "warning"
    };
    return <Chip label={capitalize(status)} color={colors[status]} size="small" />;
  };

  const renderRequestCard = (request, index) => (
    <Zoom in={!isLoading} className={`request-card-zoom request-card-zoom-${index}`}>
      <Grid item xs={12} sm={6} md={4} lg={3} key={request._id}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.05) 100%)',
            border: '1px solid',
            borderColor: 'divider',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            '&:hover': {
              borderColor: 'primary.main',
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 40px rgba(0, 161, 221, 0.2)',
            }
          }}
        >
          {/* Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                boxShadow: '0 4px 16px rgba(0, 161, 221, 0.3)'
              }}
            >
              <RequestQuoteIcon sx={{ fontSize: 24, color: 'white' }} />
            </Avatar>
            <Chip
              label={formatCash(request.amount || 0)}
              size="small"
              sx={{
                backgroundColor: 'rgba(76, 175, 80, 0.2)',
                color: '#4caf50',
                fontWeight: 600,
                border: '1px solid',
                borderColor: 'rgba(76, 175, 80, 0.3)'
              }}
            />
          </Stack>

          {/* Content */}
          <Box sx={{ mb: 3, flexGrow: 1 }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
              {capitalize(TrimText(request?.userId?.username || 'N/A', 30))}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              {request?.userId?.email || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              IP: {request?.userIpAddress || 'N/A'}
            </Typography>
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {getSplitDetails(request).length > 1
                  ? `Payment Methods (${getSplitDetails(request).length})`
                  : 'Payment Method'}
              </Typography>
              <PaymentBreakdown request={request} />
            </Box>
            <Box sx={{ mb: 2 }}>
              {statusChip(request.status)}
            </Box>
            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
              Created: {fDate(request.createdAt)}
            </Typography>
          </Box>

          {/* Actions - the status is already shown above, so when there is
              nothing to act on the card simply ends after the details. */}
          {request.status === "pending" && user?.role === ADMIN_ROLES.SUPER_ADMIN && (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => handleApprovalAction(request, "approved")}
                sx={{
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #43a047 0%, #5cb85c 100%)',
                  }
                }}
              >
                Approve
              </Button>
              <Button
                size="small"
                variant="contained"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => handleApprovalAction(request, "rejected")}
                sx={{
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #e53935 0%, #e57373 100%)',
                  }
                }}
              >
                Reject
              </Button>
            </Stack>
          )}
        </Paper>
      </Grid>
    </Zoom>
  );

  const columns = [
    { name: "S.No.", cell: (_, i) => (currentPage - 1) * pageSize + i + 1, width: "80px" },
    { name: "Username", selector: row => capitalize(TrimText(row?.userId?.username || 'N/A', 25)), sortable: true },
    { name: "Email", selector: row => row?.userId?.email || '-', sortable: true },
    { name: "Amount", selector: row => row.amount ? formatCash(row.amount) : '-', sortable: true },
    {
      name: "Payment Method",
      cell: row => <PaymentBreakdown request={row} />,
      wrap: true,
      minWidth: "190px",
    },
    { name: "IP Address", selector: row => row?.userIpAddress || '-', sortable: true },
    { name: "Status", cell: row => statusChip(row.status), sortable: true },
    { name: "Created At", selector: row => fDate(row.createdAt), sortable: true },
    ...(user?.role === ADMIN_ROLES.SUPER_ADMIN ? [{
      name: "Action", center: true,
      cell: row => row.status === "pending" ? (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Approve">
            <IconButton color="success" onClick={() => handleApprovalAction(row, "approved")}>
              <CheckCircleIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reject">
            <IconButton color="error" onClick={() => handleApprovalAction(row, "rejected")}>
              <CancelIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>-</Typography>
      )
    }] : [])
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Helmet><title>Payment Withdrawal Request</title></Helmet>
      <Fade in timeout={500}>
        <Stack spacing={4}>
          {/* Header Section */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
              border: '1px solid',
              borderColor: 'divider',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #00a1dd 0%, #42a5f5 50%, #90caf9 100%)',
              }
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={3}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                    boxShadow: '0 8px 32px rgba(0, 161, 221, 0.4)'
                  }}
                >
                  <RequestQuoteIcon sx={{ fontSize: 32, color: 'white' }} />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                    Payment Withdrawal Request
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Manage and process user withdrawal requests
                  </Typography>
                </Box>
              </Stack>
            </Stack>

            {/* Statistics Cards */}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                    {totalRequests}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Total Requests
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'rgba(255, 193, 7, 0.1)',
                    border: '1px solid rgba(255, 193, 7, 0.3)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: '#ffc107', fontWeight: 700 }}>
                    {pendingRequests}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Pending
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'rgba(76, 175, 80, 0.1)',
                    border: '1px solid rgba(76, 175, 80, 0.3)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 700 }}>
                    {approvedRequests}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Approved
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'rgba(244, 67, 54, 0.1)',
                    border: '1px solid rgba(244, 67, 54, 0.3)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: '#f44336', fontWeight: 700 }}>
                    {rejectedRequests}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Rejected
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>

          {/* Controls Section */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap', gap: 2 }}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
                <TableFilter data={PAYMENT_WITHDRAWAL_STATUS_OPTIONS} value={status} setValue={setStatus} filterName="Status" />
                <Input
                  value={searchItem}
                  placeholder="Search requests..."
                  onChange={handleSearchChange}
                  sx={{
                    color: 'white',
                    '& .MuiInput-input': {
                      color: 'white',
                    },
                    '& .MuiInput-input::placeholder': {
                      color: 'rgba(255, 255, 255, 0.5)',
                      opacity: 1,
                    },
                    '& .MuiInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 2,
                      padding: '8px 16px',
                    }
                  }}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="clear-search"
                        onClick={() => {
                          setSearchItem("");
                          fetchUsers("");
                        }}
                        sx={{ color: 'text.secondary' }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </Stack>

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode) {
                    setViewMode(newMode);
                  }
                }}
                sx={{
                  '& .MuiToggleButton-root': {
                    color: 'text.secondary',
                    borderColor: 'divider',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      }
                    }
                  }
                }}
              >
                <ToggleButton value="grid">
                  <ViewModuleIcon />
                </ToggleButton>
                <ToggleButton value="list">
                  <ViewListIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Paper>

          {/* Content Section */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            }}
          >
            {viewMode === 'grid' ? (
              /* Grid View */
              <Box sx={{ p: 3 }}>
                {isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <Loader />
                  </Box>
                ) : !data || data.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 3,
                        background: 'linear-gradient(135deg, #9c27b0 0%, #e91e63 100%)',
                        boxShadow: '0 8px 32px rgba(156, 39, 176, 0.3)'
                      }}
                    >
                      <RequestQuoteIcon sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                      No withdrawal requests found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting your search criteria
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Grid container spacing={3}>
                      {data.map((request, index) => renderRequestCard(request, index))}
                    </Grid>

                    {/* Pagination Controls (Grid View) */}
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 2,
                      flexWrap: 'wrap',
                      mt: 4,
                      p: 2,
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}>
                      {/* Center - Item Range Display */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          {totalCount > 0 ? (
                            `Showing ${((currentPage || 1) - 1) * pageSize + 1} to ${Math.min((currentPage || 1) * pageSize, totalCount)} of ${totalCount} requests`
                          ) : (
                            'No requests found'
                          )}
                        </Typography>
                      </Box>
                      {/* Left side - Page Size Selector */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: 'white', whiteSpace: 'nowrap' }}>
                          Items per page:
                        </Typography>
                        <FormControl size="small">
                          <Select
                            value={pageSize}
                            onChange={(e) => {
                              setPageSize(e.target.value);
                              setCurrentPage(1);
                            }}
                            displayEmpty
                            sx={{
                              color: 'white',
                              minWidth: 80,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255, 255, 255, 0.5)',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255, 255, 255, 0.7)',
                              },
                              '& .MuiSvgIcon-root': {
                                color: 'white',
                              },
                            }}
                          >
                            <MenuItem value={2}>2</MenuItem>
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      {/* Right side - Navigation Controls */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={totalPages === 0 || (currentPage || 1) === 1}
                          onClick={() => setCurrentPage((currentPage || 1) - 1)}
                          sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                        >
                          Previous
                        </Button>
                        <Typography variant="body2" sx={{ color: 'white', mx: 1 }}>
                          {totalCount > 0 ? (
                            `Page ${currentPage || 1} of ${totalPages}`
                          ) : (
                            'No data'
                          )}
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={totalPages === 0 || (currentPage || 1) >= totalPages}
                          onClick={() => setCurrentPage((currentPage || 1) + 1)}
                          sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                        >
                          Next
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            ) : (
              /* List View */
              <Box>
                <DataTable
                  customStyles={{
                    headCells: {
                      style: {
                        fontSize: "15px",
                        fontWeight: "bold",
                        backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        color: 'black'
                      },
                    },
                    rows: {
                      style: {
                        backgroundColor: 'transparent',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        }
                      }
                    },
                    cells: {
                      style: {
                        color: 'white',
                      }
                    }
                  }}
                  columns={columns}
                  data={data}
                  expandableRows
                  expandableRowsComponent={SplitDetailsRow}
                  expandableRowDisabled={row => getSplitDetails(row).length === 0}
                  pagination
                  paginationServer
                  paginationTotalRows={totalCount || 0}
                  paginationCurrentPage={currentPage || 1}
                  paginationDefaultPage={currentPage || 1}
                  onChangePage={setCurrentPage}
                  paginationPerPage={pageSize}
                  paginationRowsPerPageOptions={[10, 20, 50]}
                  onChangeRowsPerPage={setPageSize}
                  paginationComponentOptions={{
                    rowsPerPageText: 'Rows per page:',
                    rangeSeparatorText: 'of',
                    selectAllRowsItem: true,
                    selectAllRowsItemText: 'All',
                  }}
                  progressPending={isLoading}
                  progressComponent={<Loader />}
                  className="custom"
                />
              </Box>
            )}
          </Paper>
        </Stack>
      </Fade>

      {/* Approval Dialog */}
      <Dialog open={approvalDialog.open} onClose={() => setApprovalDialog({ open: false, type: null, request: null, message: "" })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: approvalDialog.type === "approved" ? "success.main" : "error.main", color: "white" }}>
          {approvalDialog.type === "approved" ? "Approve Withdrawal" : "Reject Withdrawal"}
        </DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <Typography><strong>Username:</strong> {capitalize(TrimText(approvalDialog.request?.userId?.username, 25))}</Typography>
          <Typography><strong>Email:</strong> {approvalDialog.request?.userId?.email || '-'}</Typography>
          <Typography><strong>IP Address:</strong> {approvalDialog.request?.userIpAddress || '-'}</Typography>
          <Box sx={{ mt: 1 }}>
            <Typography>
              <strong>
                {getSplitDetails(approvalDialog.request).length > 1
                  ? `Payment Methods (${getSplitDetails(approvalDialog.request).length}):`
                  : 'Payment Method:'}
              </strong>
            </Typography>
            <Box sx={{ maxWidth: 280 }}>
              <PaymentBreakdown request={approvalDialog.request} />
            </Box>
          </Box>
          <Typography sx={{ mt: 1 }}><strong>Total:</strong></Typography>
          <Chip label={formatCash(approvalDialog.request?.amount || 0)} color="primary" size="medium" sx={{ mt: 0.5 }} />
          <Typography sx={{ mt: 2 }}>
            {approvalDialog.type === "approved"
              ? "Are you sure you want to approve this request?"
              : "Please confirm the rejection of this request."}
          </Typography>
          {approvalDialog.type === "rejected" && (
            <TextField
              fullWidth multiline rows={3} sx={{ mt: 2 }}
              placeholder="Enter rejection reason..."
              value={approvalDialog.message}
              onChange={e => setApprovalDialog(prev => ({ ...prev, message: e.target.value }))}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog({ open: false, type: null, request: null, message: "" })}>Cancel</Button>
          <Button variant="contained" color={approvalDialog.type === "approved" ? "success" : "error"} onClick={confirmApprovalAction}>
            {approvalDialog.type === "approved" ? "Approve" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
